import logoUrl from '../../assets/d3-logo.png';
import { api, fetchConfig } from '../../lib/api.js';
import { authState } from '../../lib/auth.js';
import { BaseComponent } from '../../lib/base-component.js';
import { html, nothing } from '../../lib/lit.js';
import { badgeText, UNREAD_POLL_INTERVAL_MS } from '../../lib/notifications.js';
import { unansweredCount } from '../../lib/polls.js';
import { router } from '../../lib/router.js';
import './community-footer.js';
import './community-changelog.js';
import './community-give-feedback.js';
import './community-item-list.js';
import './community-item-page.js';
import './community-legal-page.js';
import './community-list-link.js';
import './community-navbar.js';
import './community-notifications.js';
import './community-poll-page.js';
import './community-polls.js';
import './community-settings.js';
import './community-unsubscribe.js';

// Register the SPA routes once at module load. /items/:id is the anchor stage 2
// links GitHub issues back to, so its shape is effectively permanent.
router.addRoute('/items/:id', 'item');
router.addRoute('/my-feedback', 'my-feedback');
router.addRoute('/changelog', 'changelog');
router.addRoute('/polls/:id', 'poll');
router.addRoute('/polls', 'polls');
router.addRoute('/terms', 'terms');
router.addRoute('/privacy', 'privacy');
router.addRoute('/notifications', 'notifications');
router.addRoute('/settings', 'settings');
// The landing page for an email's unsubscribe link. Public: see render().
router.addRoute('/unsubscribe', 'unsubscribe');
// Where the public lists' email links land (confirm, leave). Public, like
// /unsubscribe: someone on a list has no account.
router.addRoute('/lists/:list/:id/:action', 'list-link');
router.addRoute('/', 'home');

/**
 * Root application shell. Loads the public client config, initialises d3-auth
 * (OAuth2 + PKCE), and renders either the signed-out landing card or the
 * signed-in shell.
 *
 * Access is invite-gated at two levels and the difference matters to what this
 * renders. Holding `access:community` means a d3-auth account was granted portal
 * access, and is what makes `GET /me` succeed at all. Which *products* the user
 * can file feedback about is separate — it comes from their per-product
 * playtester grant, and an empty list is a legitimate state (invited, but not to
 * anything yet), not an error.
 */
export class CommunityApp extends BaseComponent {
    static properties = {
        _loading: { state: true },
        _authed: { state: true },
        _me: { state: true },
        _error: { state: true },
        _signInUnavailable: { state: true },
        _avatarFailed: { state: true },
        _appVersion: { state: true },
        _authBaseUrl: { state: true },
        _authTenantSlug: { state: true },
        _route: { state: true },
        _items: { state: true },
        _recent: { state: true },
        _changelog: { state: true },
        _polls: { state: true },
        _itemsLoading: { state: true },
        _composing: { state: true },
        _params: { state: true },
        _unread: { state: true },
    };

    constructor() {
        super();
        this._loading = true;
        this._authed = false;
        /** @type {object|null} */
        this._me = null;
        /** @type {string} */
        this._error = '';
        this._signInUnavailable = false;
        this._avatarFailed = false;
        /** @type {string} deployed API version, surfaced in the footer (from /config) */
        this._appVersion = '';
        this._authBaseUrl = '';
        this._authTenantSlug = '';
        this._route = router.currentRoute;
        /** @type {Array<object>} */
        this._items = [];
        /** @type {Array<object>} */
        this._recent = [];
        /** @type {Array<object>} */
        this._changelog = [];
        /** @type {Array<object>} open polls first, then closed ones */
        this._polls = [];
        this._itemsLoading = false;
        this._composing = false;
        this._params = router.params;
        /** @type {number} unread notifications, for the navbar badge */
        this._unread = 0;
        /** @type {ReturnType<typeof setInterval>|null} */
        this._unreadTimer = null;
        this._onVisibilityChange = () => {
            if (document.visibilityState === 'visible') this._refreshUnread();
        };
    }

    async connectedCallback() {
        super.connectedCallback();

        this._unsubscribeAuth = authState.subscribe((state) => {
            this._loading = state.loading;
            this._authed = state.isAuthenticated;
            if (state.isAuthenticated) this._loadMe();
            else this._stopUnreadPolling();
        });
        this._unsubscribeRouter = router.subscribe((state) => {
            this._route = state.route;
            this._params = state.params;
        });

        try {
            const config = await fetchConfig();
            this._appVersion = config.version ?? '';
            this._authBaseUrl = config.auth?.baseUrl ?? '';
            this._authTenantSlug = config.auth?.tenantSlug ?? '';

            // The client id and tenant slug are deployment facts with no safe
            // default (see docs/provisioning.md). When d3-auth has not been
            // provisioned yet the API reports them empty, and sign-in genuinely
            // cannot work — say so rather than rendering a button that fails.
            if (!config.auth?.clientId || !config.auth?.tenantSlug) {
                this._signInUnavailable = true;
                this._loading = false;
                return;
            }

            // Mapped explicitly rather than spread: the API's /config groups auth
            // settings under `auth`, and d3-kit's AuthConfig is flat. An explicit
            // map means adding a config field can't silently shadow one of these.
            await authState.init({
                d3AuthBaseURL: config.auth.baseUrl,
                d3AuthFrontendURL: config.auth.frontendUrl,
                d3AuthClientID: config.auth.clientId,
                d3AuthAudience: config.auth.audience,
                d3AuthTenantSlug: config.auth.tenantSlug,
                appName: 'community',
            });

            router.handleRoute();
        } catch (err) {
            this._error = String(err);
            this._loading = false;
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._unsubscribeAuth?.();
        this._unsubscribeRouter?.();
        this._stopUnreadPolling();
    }

    /**
     * Keep the badge current: now, every minute while the tab is visible, and
     * whenever the tab comes back into view. A hidden tab does not poll — the
     * count is only worth fetching for someone who can see it.
     */
    _startUnreadPolling() {
        if (this._unreadTimer) return;
        this._refreshUnread();
        this._unreadTimer = setInterval(() => {
            if (document.visibilityState === 'visible') this._refreshUnread();
        }, UNREAD_POLL_INTERVAL_MS);
        document.addEventListener('visibilitychange', this._onVisibilityChange);
    }

    _stopUnreadPolling() {
        if (this._unreadTimer) clearInterval(this._unreadTimer);
        this._unreadTimer = null;
        document.removeEventListener('visibilitychange', this._onVisibilityChange);
    }

    async _refreshUnread() {
        try {
            const result = await api.get('/notifications/unread-count');
            this._unread = result?.count ?? 0;
        } catch {
            // A missed badge refresh is not worth an error on the page. The next
            // poll tries again, and the notifications page shows the truth.
        }
    }

    /** @param {Event} event @param {string} path */
    _navigate(event, path) {
        event.preventDefault();
        router.navigate(path);
    }

    async _loadMe() {
        try {
            this._me = await api.get('/me');
            this._error = '';
            this._loadItems();
            this._loadPolls();
            this._startUnreadPolling();
        } catch (err) {
            // A 403 here is the invite gate doing its job: a valid d3-auth
            // account without `access:community` on this API. That is a
            // different message from a broken backend, and conflating them would
            // send an uninvited person hunting for a bug.
            this._error =
                /** @type {any} */ (err).status === 403
                    ? 'Your account does not have access to the community portal.'
                    : String(err);
        }
    }

    async _loadItems() {
        this._itemsLoading = true;
        try {
            const [mine, recent] = await Promise.all([api.get('/items/mine'), api.get('/items')]);
            this._items = mine?.items ?? [];
            this._recent = recent?.items ?? [];
        } catch (err) {
            this._error = String(err);
        } finally {
            this._itemsLoading = false;
        }
    }

    /**
     * The polls list, for the Polls tab and the nudge on the home page. Loaded
     * apart from the items so a slow or failing poll list cannot hold up the
     * feedback the page is mostly about.
     */
    async _loadPolls() {
        try {
            const result = await api.get('/polls');
            this._polls = result?.polls ?? [];
        } catch (err) {
            this._error = String(err);
        }
    }

    /** Prepend a just-filed item rather than refetching: the server returned the
     *  created row, and the list is ordered newest-first, so the answer is known. */
    _onItemFiled(event) {
        this._items = [event.detail, ...this._items];
        this._recent = [event.detail, ...this._recent];
        this._composing = false;
    }

    /** The full-height app frame every state renders inside. */
    _renderShell(body) {
        return html`
            <div class="vh-100 d-flex flex-column bg-body-tertiary overflow-hidden">${body}</div>
        `;
    }

    _appFooter() {
        return html`<community-footer .version=${this._appVersion}></community-footer>`;
    }

    /** The shared <main> scroll region for the signed-in content pages. */
    _renderMain(content) {
        return html`
            <main class="flex-grow-1 d-flex flex-column overflow-auto" style="min-height: 0;">
                ${content}
            </main>
        `;
    }

    /**
     * The legal pages are public: reachable signed-out, by footer link or direct
     * URL. They render in their own chrome rather than the signed-in shell,
     * because a signed-out visitor has no navbar.
     * @param {string} docKey
     */
    _renderLegal(docKey) {
        return this._renderShell(html`
            ${this._authed ? this._renderNav() : nothing}
            ${this._renderMain(html`
                <div class="py-4">
                    <community-legal-page
                        .docKey=${docKey}
                        .authBaseUrl=${this._authBaseUrl}
                        .tenantSlug=${this._authTenantSlug}
                    ></community-legal-page>
                </div>
                ${this._appFooter()}
            `)}
        `);
    }

    _renderListLink() {
        return this._renderShell(html`
            ${this._authed ? this._renderNav() : nothing}
            ${this._renderMain(html`
                <community-list-link
                    .list=${this._params?.list ?? ''}
                    .entryId=${this._params?.id ?? ''}
                    .action=${this._params?.action ?? ''}
                ></community-list-link>
                ${this._appFooter()}
            `)}
        `);
    }

    _renderUnsubscribe() {
        return this._renderShell(html`
            ${this._authed ? this._renderNav() : nothing}
            ${this._renderMain(html`<community-unsubscribe></community-unsubscribe>${this._appFooter()}`)}
        `);
    }

    _renderLoading() {
        return this._renderShell(html`
            <div class="flex-grow-1 d-flex align-items-center justify-content-center">
                <div class="spinner-border text-secondary" role="status">
                    <span class="visually-hidden">Loading…</span>
                </div>
            </div>
        `);
    }

    _renderSignedOut() {
        return this._renderShell(html`
            <div
                class="flex-grow-1 overflow-auto d-flex flex-column align-items-center justify-content-center p-4"
            >
                ${this._renderSignInCard()}
            </div>
            ${this._appFooter()}
        `);
    }

    _renderSignInCard() {
        return html`
            <div class="card shadow-sm border-0" style="max-width: 26rem;">
                <div class="card-body text-center p-5">
                    <img
                        src=${logoUrl}
                        alt=""
                        class="mb-3"
                        style="height: 48px; width: auto;"
                    />
                    <h1 class="h3 mb-1">Dice Driven Community</h1>
                    <p class="text-muted mb-4">
                        Report bugs, request features, and follow what ships.
                    </p>
                    ${
                        this._signInUnavailable
                            ? html`
                                  <div class="alert alert-warning text-start small mb-0">
                                      <strong>Sign-in isn't configured yet.</strong>
                                      Set <code>D3_AUTH_CLIENT_ID</code> and
                                      <code>D3_AUTH_TENANT_SLUG</code> on the API and reload.
                                  </div>
                              `
                            : html`
                                  <div class="mx-auto" style="max-width: 22rem;">
                                      <button
                                          class="btn btn-primary btn-lg w-100"
                                          @click=${() => authState.login()}
                                      >
                                          <i class="bi bi-box-arrow-in-right me-2"></i>Sign In
                                      </button>
                                  </div>
                              `
                    }
                </div>
            </div>
        `;
    }

    /**
     * The signed-in top nav: brand, notifications, the signed-in user (which
     * opens email settings), and Sign Out.
     */
    _renderNav() {
        const badge = badgeText(this._unread);
        return html`
            <community-navbar container="container-xxl">
                <a
                    slot="actions"
                    href="/notifications"
                    class="btn btn-sm btn-outline-secondary position-relative"
                    title="Notifications"
                    aria-label=${badge ? `Notifications, ${badge} unread` : 'Notifications'}
                    @click=${(e) => this._navigate(e, '/notifications')}
                >
                    <i class="bi bi-bell"></i>
                    ${
                        badge
                            ? html`<span
                                  class="position-absolute top-0 start-100 translate-middle badge rounded-pill text-bg-danger"
                                  >${badge}</span
                              >`
                            : nothing
                    }
                </a>
                <a
                    slot="actions"
                    href="/settings"
                    class="d-flex align-items-center gap-2 text-decoration-none"
                    title="Email settings"
                    @click=${(e) => this._navigate(e, '/settings')}
                >
                    ${
                        this._me?.avatarUrl && !this._avatarFailed
                            ? html`<img
                                  src=${this._me.avatarUrl}
                                  alt=""
                                  width="28"
                                  height="28"
                                  class="rounded-circle"
                                  @error=${() => {
                                      this._avatarFailed = true;
                                  }}
                              />`
                            : ''
                    }
                    <span class="text-muted small d-none d-sm-inline"
                        >${this._me?.displayName ?? ''}</span
                    >
                </a>
                <button
                    slot="actions"
                    class="btn btn-outline-secondary btn-sm"
                    @click=${() => authState.logout()}
                >
                    <i class="bi bi-box-arrow-right me-1"></i>Sign Out
                </button>
            </community-navbar>
        `;
    }

    _renderHome() {
        return html`
            <div class="container-xxl py-4">
                ${
                    this._error
                        ? html`<div class="alert alert-danger" role="alert">${this._error}</div>`
                        : ''
                }
                ${this._renderHomeBody()}
            </div>
        `;
    }

    /**
     * A null `_me` means the profile has not arrived yet, so wait rather than
     * rendering. There is no "you hold no playtests" branch any more: holding
     * access:community is the whole gate, and every member can file against
     * every product — or none.
     */
    _renderHomeBody() {
        if (!this._me) return this._renderInlineSpinner();
        return this._renderFeedback();
    }

    _renderInlineSpinner() {
        return html`
            <div class="text-muted small d-flex align-items-center gap-2">
                <span class="spinner-border spinner-border-sm" role="status"></span>
                Loading…
            </div>
        `;
    }

    /**
     * Two shapes, because what belongs in front of you depends on whether you
     * have been here before.
     *
     * Nothing filed yet: the form IS the page — there is no list to look at, and
     * asking someone to press a button to reach the only thing they can do is
     * a step for its own sake. No tab chrome either, for the same reason.
     *
     * Something filed: tabs, because there are now several things to look at and
     * only one of them belongs in front of you at a time.
     *
     * /polls always gets the tabs, filed or not: it is a list to look at, and the
     * home page's nudge links there.
     */
    _renderFeedback() {
        const products = this._me?.products ?? [];

        if (this._itemsLoading) {
            return html`
                <div class="text-muted small d-flex align-items-center gap-2">
                    <span class="spinner-border spinner-border-sm" role="status"></span>
                    Loading your feedback…
                </div>
            `;
        }

        if (this._items.length === 0 && !this._composing && this._activeTab() !== 'polls') {
            return html`
                ${this._renderPollNudge()}
                <p class="text-muted mb-3">
                    Found a bug, want something, or just have a thought? Tell us.
                </p>
                <community-give-feedback
                    .products=${products}
                    @item-filed=${this._onItemFiled}
                ></community-give-feedback>
                ${this._renderChangelogList()} ${this._renderAllList()}
            `;
        }

        return html`
            ${
                this._composing
                    ? html`<community-give-feedback
                          .products=${products}
                          .dismissible=${true}
                          heading="New feedback"
                          @item-filed=${this._onItemFiled}
                          @cancel=${() => {
                              this._composing = false;
                          }}
                      ></community-give-feedback>`
                    : ''
            }
            ${this._renderTabs()}
            <div class="tab-content bg-body border border-top-0 rounded-bottom mb-4 overflow-hidden">
                ${this._renderActivePanel()}
            </div>
        `;
    }

    /**
     * Open polls the member has not answered, pointed out on the page someone
     * who has filed nothing lands on — where there are no tabs to find the
     * Polls tab in.
     */
    _renderPollNudge() {
        const count = unansweredCount(this._polls);
        if (count === 0) return nothing;
        return html`
            <div class="alert alert-primary d-flex align-items-center gap-2 mb-4">
                <i class="bi bi-bar-chart"></i>
                <span class="flex-grow-1">
                    ${count === 1 ? 'There is a poll' : `There are ${count} polls`} waiting for
                    your answer.
                </span>
                <a class="btn btn-sm btn-primary" href="/polls">Answer</a>
            </div>
        `;
    }

    /** Which tab the current route selects. `/` is All Feedback. */
    _activeTab() {
        if (this._route === 'my-feedback') return 'my';
        if (this._route === 'changelog') return 'changelog';
        if (this._route === 'polls') return 'polls';
        return 'all';
    }

    /**
     * The tab bar. Routed rather than local state, so a tab is linkable and the
     * back button works.
     *
     * The Changelog tab is omitted entirely when nothing is published, for the
     * same reason the panel used to be: an empty changelog reads as a broken
     * page rather than a young product. The Polls tab likewise, until there is
     * a poll — and it counts the open ones you have not answered.
     *
     * "Give feedback" sits with the tabs rather than inside My Feedback, so the
     * primary action is reachable from every tab instead of only one.
     */
    _renderTabs() {
        const active = this._activeTab();
        const tab = (id, path, label, badge = 0) => html`
            <li class="nav-item">
                <a
                    class="nav-link ${active === id ? 'active' : ''}"
                    href=${path}
                    aria-current=${active === id ? 'page' : nothing}
                >${label}${
                    badge > 0
                        ? html` <span class="badge rounded-pill text-bg-primary">${badge}</span>`
                        : nothing
                }</a>
            </li>
        `;

        return html`
            <ul class="nav nav-tabs">
                ${tab('all', '/', 'All feedback')}
                ${tab('my', '/my-feedback', 'My feedback')}
                ${this._changelog.length > 0 ? tab('changelog', '/changelog', 'Changelog') : nothing}
                ${
                    this._polls.length > 0 || active === 'polls'
                        ? tab('polls', '/polls', 'Polls', unansweredCount(this._polls))
                        : nothing
                }
                <li class="nav-item ms-auto d-flex align-items-center pb-2">
                    <button
                        class="btn btn-primary btn-sm"
                        ?disabled=${this._composing}
                        @click=${() => {
                            this._composing = true;
                        }}
                    >
                        <i class="bi bi-plus-lg me-1"></i>Give feedback
                    </button>
                </li>
            </ul>
        `;
    }

    _renderActivePanel() {
        switch (this._activeTab()) {
            case 'my':
                return this._renderMyList();
            case 'changelog':
                return this._renderChangelogList();
            case 'polls':
                return html`<community-polls .polls=${this._polls}></community-polls>`;
            default:
                return this._renderAllList();
        }
    }

    _renderMyList() {
        return html`
            <community-item-list
                .items=${this._items}
                .flush=${true}
                empty="You haven't filed anything yet."
            ></community-item-list>
        `;
    }

    /**
     * Everything raised across the community, this caller's own items included.
     *
     * Unlike the old stacked region this is NOT hidden when it would only echo
     * your own list: a tab that renders nothing when selected is a bug, not a
     * tidy-up. The empty string carries that case instead.
     */
    _renderAllList() {
        return html`
            <community-item-list
                .items=${this._recent}
                .showAuthor=${true}
                .flush=${true}
                empty="Nothing has been filed yet."
            ></community-item-list>
        `;
    }

    _renderChangelogList() {
        if (this._changelog.length === 0) return '';
        return html`<community-changelog .posts=${this._changelog}></community-changelog>`;
    }

    _renderRoute() {
        if (this._route === 'notifications') {
            return html`
                <div class="container-xxl py-4">
                    <community-notifications
                        @unread-changed=${() => this._refreshUnread()}
                    ></community-notifications>
                </div>
            `;
        }
        if (this._route === 'settings') {
            return html`
                <div class="container-xxl py-4" style="max-width: 48rem;">
                    <community-settings></community-settings>
                </div>
            `;
        }
        if (this._route === 'poll') {
            return html`
                <div class="container-xxl py-4" style="max-width: 56rem;">
                    <community-poll-page
                        .pollId=${this._params?.id ?? ''}
                        @poll-answered=${() => this._loadPolls()}
                    ></community-poll-page>
                </div>
            `;
        }
        if (this._route === 'item') {
            return html`
                <div class="container-xxl py-4">
                    <community-item-page
                        .itemId=${this._params?.id ?? ''}
                        .isStaff=${this._me?.isStaff ?? false}
                        .products=${this._me?.products ?? []}
                    ></community-item-page>
                </div>
            `;
        }
        return this._renderHome();
    }

    _renderSignedIn() {
        return this._renderShell(html`
            ${this._renderNav()}
            ${this._renderMain(html`${this._renderRoute()}${this._appFooter()}`)}
        `);
    }

    render() {
        if (this._loading) return this._renderLoading();
        // Before the auth gate: these are public pages, and a signed-out
        // visitor following the footer link must reach them.
        if (this._route === 'terms') return this._renderLegal('community-terms');
        if (this._route === 'privacy') return this._renderLegal('community-privacy');
        // Also before the gate: an email's unsubscribe link must work on a
        // device where nobody is signed in.
        if (this._route === 'unsubscribe') return this._renderUnsubscribe();
        if (this._route === 'list-link') return this._renderListLink();
        if (!this._authed) return this._renderSignedOut();
        return this._renderSignedIn();
    }
}

customElements.define('community-app', CommunityApp);
